/**
 * Central affiliate configuration.
 *
 * Every affiliate URL flows through this file so activating a program is a
 * one-line swap in `frontend/.env` (no code changes required).
 *
 * ┌─────────── HOW TO ACTIVATE ───────────┐
 *
 * 1. SpotHero — airport parking (~$3-5/booking)
 *    Sign up at https://spothero.com/affiliates (managed via Impact.com).
 *    Once approved, Impact.com will give you a tracking domain such as
 *    `spothero.sjv.io/YOUR_ID`. Put it in .env:
 *      REACT_APP_SPOTHERO_TRACKING_URL=https://spothero.sjv.io/c/YOUR_ID
 *    All ParkingWidget clicks will start earning commission automatically.
 *
 * 2. CardRatings — travel credit cards ($50-150/approval)
 *    Sign up at https://www.cardratings.com/publishers.html.
 *    They give you an affiliate ID that appears as `?a=YOUR_ID` on card URLs.
 *    Put it in .env:
 *      REACT_APP_CARDRATINGS_AFFILIATE_ID=YOUR_ID
 *    Blog credit-card mentions will start driving tracked clicks.
 *
 * Until IDs are configured, the site still links to the correct destination
 * pages — visitors get value, we just don't earn commission yet.
 * ────────────────────────────────────────
 */

// ── SpotHero (parking) ─────────────────────────────────────────────
const SPOTHERO_TRACKING_URL = process.env.REACT_APP_SPOTHERO_TRACKING_URL || '';

/**
 * Build a deep-link to SpotHero's airport parking search for a given IATA.
 * Uses UTM tags so we can measure clicks in Google Analytics even before
 * the Impact.com tracking domain is configured.
 */
export function spotHeroUrl(iataCode, { airportName = '' } = {}) {
  const code = (iataCode || '').toUpperCase();
  // Canonical SpotHero deep link. Works for every US IATA + many international.
  const deepLink =
    `https://spothero.com/search?kind=airport` +
    `&search_string=${encodeURIComponent(code + ' airport' + (airportName ? ` ${airportName}` : ''))}` +
    `&utm_source=howbusyismyairport` +
    `&utm_medium=referral` +
    `&utm_campaign=parking_widget` +
    `&utm_content=${code}`;

  // When the Impact.com tracking URL is set, wrap the deep link through it.
  if (SPOTHERO_TRACKING_URL) {
    return `${SPOTHERO_TRACKING_URL}?u=${encodeURIComponent(deepLink)}`;
  }
  return deepLink;
}

// ── CardRatings (credit cards) ─────────────────────────────────────
const CARDRATINGS_AFFILIATE_ID = process.env.REACT_APP_CARDRATINGS_AFFILIATE_ID || '';

/**
 * Curated map of card name → CardRatings destination page. Only cards we
 * actually mention in blog posts. The URL is real; adding an affiliate ID
 * just makes CardRatings pay commission when the visitor is approved.
 *
 * Card names are matched case-insensitively against blog HTML.
 */
const CARDS = [
  {
    match: /Chase\s+Sapphire\s+Reserve/gi,
    url: 'https://www.cardratings.com/chase-sapphire-reserve.html',
  },
  {
    match: /Chase\s+Sapphire\s+Preferred/gi,
    url: 'https://www.cardratings.com/chase-sapphire-preferred.html',
  },
  {
    match: /American\s+Express\s+Platinum(?:\s+Card)?/gi,
    url: 'https://www.cardratings.com/american-express-platinum.html',
  },
  {
    match: /Amex\s+Platinum(?:\s+Card)?/gi,
    url: 'https://www.cardratings.com/american-express-platinum.html',
  },
  {
    match: /American\s+Express\s+Gold(?:\s+Card)?/gi,
    url: 'https://www.cardratings.com/american-express-gold.html',
  },
  {
    match: /Amex\s+Gold/gi,
    url: 'https://www.cardratings.com/american-express-gold.html',
  },
  {
    match: /American\s+Express\s+Green(?:\s+Card)?/gi,
    url: 'https://www.cardratings.com/american-express-green.html',
  },
  {
    match: /Amex\s+Centurion(?:\s+Card)?/gi,
    url: 'https://www.cardratings.com/travel-credit-cards.html',
  },
  {
    match: /Capital\s+One\s+Venture\s+X/gi,
    url: 'https://www.cardratings.com/capital-one-venture-x.html',
  },
  {
    match: /Capital\s+One\s+Venture/gi,
    url: 'https://www.cardratings.com/capital-one-venture.html',
  },
  {
    match: /Citi\s+Prestige/gi,
    url: 'https://www.cardratings.com/travel-credit-cards.html',
  },
  {
    match: /Delta\s+SkyMiles\s+Reserve(?:\s+Amex)?/gi,
    url: 'https://www.cardratings.com/american-express-delta-reserve.html',
  },
  {
    match: /Delta\s+Reserve/gi,
    url: 'https://www.cardratings.com/american-express-delta-reserve.html',
  },
  {
    match: /United\s+Club\s+Infinite/gi,
    url: 'https://www.cardratings.com/travel-credit-cards.html',
  },
];

function buildCardRatingsUrl(baseUrl) {
  const params = [
    `utm_source=howbusyismyairport`,
    `utm_medium=blog`,
    `utm_campaign=credit_card_affiliate`,
  ];
  if (CARDRATINGS_AFFILIATE_ID) {
    params.unshift(`a=${encodeURIComponent(CARDRATINGS_AFFILIATE_ID)}`);
  }
  return `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}${params.join('&')}`;
}

/**
 * Rewrite blog HTML: turn known credit-card names into <a> tags pointing to
 * CardRatings with tracking. Skips names that are already inside an <a>.
 *
 * Only tags the FIRST occurrence per card per post (avoids link spam).
 */
export function linkifyCards(html) {
  if (!html || typeof html !== 'string') return html;
  let out = html;
  const seen = new Set();
  for (const card of CARDS) {
    const re = new RegExp(card.match.source, card.match.flags);
    let matched = false;
    out = out.replace(re, (name, offset) => {
      // Skip if already wrapped in an anchor (avoid nested <a>).
      const preceding = out.slice(Math.max(0, offset - 120), offset);
      if (/<a[^>]*$/i.test(preceding)) return name;
      // Only tag the first hit per card per post.
      if (matched) return name;
      matched = true;
      const url = buildCardRatingsUrl(card.url);
      return `<a href="${url}" target="_blank" rel="sponsored nofollow noopener" class="affiliate-link">${name}</a>`;
    });
    if (matched) seen.add(card.url);
  }
  return out;
}

/** Does a post contain any credit-card affiliate links? (drives the disclosure banner) */
export function hasCardAffiliates(html) {
  if (!html || typeof html !== 'string') return false;
  return CARDS.some((c) => new RegExp(c.match.source, c.match.flags).test(html));
}
